import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { type RumView, type SessionView } from '@cercle/shared';
import { useApi, useApp } from '@/shared/app-context';
import { errorMessage } from '@/shared/http/http-client';
import { useAsync } from '@/shared/hooks/useAsync';
import { useSessionSocket } from '@/shared/ws/useSessionSocket';
import {
  Avatar,
  Badge,
  Bottle,
  Button,
  Card,
  EmptyState,
  ErrorText,
  Kicker,
  Muted,
  Panel,
  Skeleton,
  Switch,
} from '@/shared/ui';
import { useCountdown } from './useCountdown';
import { formatSessionTimeRange, splitSessionDate } from './format';
import styles from './session.module.scss';

/**
 * Écran 01 — l'accueil : la prochaine séance.
 *
 * Le rhum à l'aveugle apparaît ici sous le nom que l'API lui donne, « Rhum mystère n°2 »,
 * avec sa silhouette éteinte et son badge doré. L'écran n'a aucune logique de masquage :
 * il affiche ce qu'il reçoit, et ne reçoit rien qu'il devrait cacher.
 */
export function SessionScreen(): ReactNode {
  const api = useApi();
  const { member } = useApp();
  const { data, loading, error, reload } = useAsync(() => api.sessions.next(), []);

  // Révélation et clôture arrivent par le socket : l'écran se rafraîchit seul, sans que
  // personne n'ait à tirer pour recharger en pleine séance.
  useSessionSocket(data?.id ?? null, {
    onRumRevealed: reload,
    onSessionClosed: reload,
    onTastingOpened: reload,
  });

  if (loading) return <Skeleton />;
  if (error) return <EmptyState>{error}</EmptyState>;
  if (!data) return <EmptyState>Aucune séance planifiée pour l’instant.</EmptyState>;

  const own = data.attendances.find((a) => a.member.id === member?.id);
  const confirmed = data.attendances.filter((a) => a.confirmed);

  return (
    <div className={styles.page}>
      <SessionPanel
        session={data}
        confirmed={own?.confirmed ?? false}
        guestCount={own?.guestCount ?? 0}
        onChange={reload}
      />

      <section className={styles.programme}>
        <header className={styles.sectionHead}>
          <span>Au programme</span>
          <span>
            {data.rums.length} rhum{data.rums.length > 1 ? 's' : ''}
          </span>
        </header>

        <ul className={styles.rumList}>
          {data.rums.map((slot) => {
            const masked = slot.rum.isMasked;
            return (
              <li key={slot.rum.id}>
                <Link
                  to={masked ? `/seances/${data.id}/aveugle` : `/rhums/${slot.rum.id}`}
                  className={[styles.rumItem, masked && styles.rumItemBlind]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <Bottle muted={masked} />
                  <span
                    className={[styles.rumInfo, masked && styles.rumInfoBlind]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <strong>{slot.rum.name}</strong>
                    <span>
                      {masked
                        ? 'Fiche masquée jusqu’à la révélation'
                        : [slot.rum.country, slot.rum.style].filter(Boolean).join(' · ')}
                    </span>
                    {slot.rum.broughtBy ? (
                      <span className={styles.broughtBy}>
                        Apporté par {slot.rum.broughtBy.pseudo}
                      </span>
                    ) : null}
                  </span>
                  {masked ? (
                    <span className={styles.blindBadge}>
                      <Badge>Aveugle</Badge>
                    </span>
                  ) : null}
                </Link>
              </li>
            );
          })}
        </ul>
      </section>

      {/*
        « J'apporte une bouteille » n'apparaît qu'une fois la présence confirmée : l'API
        refuse autrement, et proposer un bouton qui refuse serait une promesse rompue.
      */}
      {own?.confirmed && data.status !== 'CLOSED' ? (
        <ContributeRum session={data} onDone={reload} />
      ) : null}

      <Attendance
        session={data}
        confirmed={own?.confirmed ?? false}
        guestCount={own?.guestCount ?? 0}
        onChange={reload}
      />

      {/*
        Les invités ne sont ni déclarables ni affichés : la fonctionnalité est suspendue
        côté interface (voir `Attendance`). Les `guestCount` déjà enregistrés dorment en
        base et ne remontent plus ici, sans quoi on annoncerait des invités que plus
        personne ne peut ni ajouter ni retirer.
      */}
      <section className={styles.presents}>
        <header className={styles.sectionHead}>
          <span>Présences confirmées</span>
          <span>
            {confirmed.length} membre{confirmed.length > 1 ? 's' : ''}
          </span>
        </header>
        <ul className={styles.avatars}>
          {confirmed.map((a) => (
            <li key={a.member.id} className={styles.presentChip} title={a.member.pseudo}>
              <Avatar pseudo={a.member.pseudo} url={a.member.avatarUrl} />
              <span className={styles.presentName}>{a.member.pseudo}</span>
            </li>
          ))}
        </ul>
      </section>

      <PartDesAnges />
    </div>
  );
}

interface SessionPanelProps {
  session: SessionView;
  confirmed: boolean;
  guestCount: number;
  onChange: () => void;
}

/**
 * L'en-tête de séance.
 *
 * Panneau en dégradé avec halo doré jusqu'à la tablette ; en desktop la maquette retire le
 * cadre et fait remonter « Je serai des vôtres » sur la ligne de titre. Le CSS s'en charge —
 * le balisage, lui, ne change pas.
 */
function SessionPanel({ session, confirmed, guestCount, onChange }: SessionPanelProps): ReactNode {
  const countdown = useCountdown(session.startsAt);
  // « Mardi 1er septembre » : l'exposant du premier du mois, comme sur la maquette.
  const { before, sup, after } = splitSessionDate(session.startsAt);

  return (
    <Panel className={styles.hero}>
      <div>
        <Kicker>Prochaine séance</Kicker>
        <div className={styles.heroDate}>
          {before}
          {sup ? <sup>{sup}</sup> : null}
          {after}
        </div>
        <div className={styles.heroPlace}>
          {formatSessionTimeRange(session.startsAt, session.endsAt)}
          {session.location ? ` · ${session.location}` : ''} · séance n°{session.number}
        </div>
      </div>

      <div className={styles.heroAside}>
        <p className={styles.countdown}>
          {countdown ? (
            <>
              <span>Dans</span>
              <strong>{countdown}</strong>
            </>
          ) : (
            <strong>{session.status === 'CLOSED' ? 'Séance clôturée' : 'C’est ce soir'}</strong>
          )}
        </p>

        {session.status !== 'CLOSED' ? (
          <span className={styles.heroCta}>
            <Attend
              session={session}
              confirmed={confirmed}
              guestCount={guestCount}
              onChange={onChange}
            />
          </span>
        ) : null}
      </div>
    </Panel>
  );
}

interface AttendanceProps {
  session: SessionView;
  confirmed: boolean;
  guestCount: number;
  onChange: () => void;
}

/**
 * US-12 — « Je serai des vôtres ».
 *
 * La déclaration d'invités est **suspendue côté interface** : plus de pastilles « +1 invité »,
 * plus de bouton « Déclarer un invité ». Rien n'a été retiré côté API — `guestCount`, le
 * réglage `maxGuests` et la route d'attendance sont intacts, et les valeurs déjà en base
 * n'ont pas été touchées. Rétablir la fonctionnalité, c'est remettre du balisage ici.
 *
 * Le composant survit à ce retrait pour cette raison : il ne fait plus qu'envelopper
 * `Attend`, mais c'est lui qui rendra la réactivation triviale.
 */
function Attendance({ session, confirmed, guestCount, onChange }: AttendanceProps): ReactNode {
  if (session.status === 'CLOSED') return null;

  return (
    <section className={styles.attendance}>
      <Attend
        session={session}
        confirmed={confirmed}
        guestCount={guestCount}
        onChange={onChange}
        block
      />
    </section>
  );
}

/**
 * « Je serai des vôtres » — le même bouton à deux endroits.
 *
 * Il vit dans l'en-tête en desktop et dans son propre bloc au pouce. Un seul composant
 * plutôt que deux : sans cela, confirmer sa présence sur un écran et pas sur l'autre
 * deviendrait un bogue qu'on ne verrait qu'en redimensionnant.
 */
function Attend({ session, confirmed, guestCount, onChange, block }: AttendProps): ReactNode {
  const api = useApi();
  const [busy, setBusy] = useState(false);

  async function toggle(): Promise<void> {
    setBusy(true);
    try {
      await api.sessions.setAttendance(session.id, {
        confirmed: !confirmed,
        guestCount: confirmed ? 0 : guestCount,
      });
      onChange();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Button
      variant={confirmed ? 'secondary' : 'primary'}
      block={block}
      disabled={busy}
      onClick={() => void toggle()}
    >
      {confirmed ? 'Je serai des vôtres ✓' : 'Je serai des vôtres'}
    </Button>
  );
}

interface AttendProps {
  session: SessionView;
  confirmed: boolean;
  guestCount: number;
  onChange: () => void;
  block?: boolean;
}

/** La part des anges : ce que le membre a déjà donné au cercle. */
function PartDesAnges(): ReactNode {
  const api = useApi();
  const { data } = useAsync(() => api.members.stats(), []);
  if (!data) return null;

  return (
    <div className={styles.anges}>
      <div className={styles.angesHead}>
        <span className={styles.angesCount}>{data.sessionsAttended}</span>
        <span className={styles.angesText}>
          <strong>Votre part des anges</strong>
          <span>
            {data.sessionsAttended} séance{data.sessionsAttended > 1 ? 's' : ''} honorée
            {data.sessionsAttended > 1 ? 's' : ''} · {data.tastingsSubmitted} note
            {data.tastingsSubmitted > 1 ? 's' : ''} déposée
            {data.tastingsSubmitted > 1 ? 's' : ''}
          </span>
        </span>
      </div>
      <p className={styles.angesQuote}>« La part des anges vous attend. »</p>
    </div>
  );
}

/**
 * « J'apporte une bouteille ».
 *
 * Le geste du bouilleur : il rattache lui-même son rhum à la séance où il vient, sans
 * passer par la régie. Il choisit dans la Cave, et peut demander que la bouteille soit
 * servie à l'aveugle — c'est tout l'intérêt d'en apporter une. Basculer l'interrupteur
 * ensuite reste une décision de régie : sinon n'importe qui démasquerait le rhum d'un
 * autre.
 *
 * Une liste déroulante native plutôt qu'une liste de boutons : la cave entière y tient,
 * et le sélecteur du téléphone sait déjà la faire défiler et filtrer à la frappe.
 */
function ContributeRum({
  session,
  onDone,
}: {
  session: SessionView;
  onDone: () => void;
}): ReactNode {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [blind, setBlind] = useState(false);
  const [rumId, setRumId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cave = useAsync(
    // Toute la cave, dégustées comprises : une séance clôturée marque ses bouteilles
    // « dégustées », mais elles restent dans la cave et peuvent revenir sur la table.
    () => (open ? api.rums.searchAll({ sort: 'name' }) : Promise.resolve([])),
    [open],
  );

  if (!open) {
    return (
      <Button variant="dashed" block onClick={() => setOpen(true)}>
        J’apporte une bouteille
      </Button>
    );
  }

  const already = new Set(session.rums.map((slot) => slot.rum.id));
  const candidates = (cave.data ?? []).filter((rum: RumView) => !already.has(rum.id));

  async function contribute(): Promise<void> {
    if (!rumId) return;
    setBusy(true);
    setError(null);
    try {
      await api.sessions.contributeRum(session.id, rumId, blind);
      setOpen(false);
      setRumId('');
      onDone();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className={styles.contribute}>
      <Kicker>Au programme de mardi</Kicker>
      <ErrorText>{error ?? cave.error}</ErrorText>

      {cave.loading ? <Skeleton lines={1} /> : null}
      {!cave.loading && !cave.error && candidates.length === 0 ? (
        <Muted>Aucune bouteille disponible dans la cave.</Muted>
      ) : null}
      {!cave.loading && candidates.length > 0 ? (
        <label className={styles.rumChoice}>
          <span>Bouteille</span>
          <select
            className={styles.select}
            value={rumId}
            disabled={busy}
            onChange={(event) => setRumId(event.target.value)}
          >
            <option value="">Choisir dans la cave…</option>
            {candidates.map((rum: RumView) => (
              <option key={rum.id} value={rum.id}>
                {[rum.name, rum.distillery, rum.country].filter(Boolean).join(' · ')}
                {rum.status === 'TASTED' ? ' (déjà dégustée)' : ''}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      <label className={styles.blindChoice}>
        <Switch
          gold
          checked={blind}
          label="Servir cette bouteille à l’aveugle"
          onChange={setBlind}
        />
        <span>À l’aveugle</span>
      </label>

      <Button block disabled={busy || !rumId} onClick={() => void contribute()}>
        {busy ? 'Ajout…' : 'Ajouter au programme'}
      </Button>

      {/*
        On part vers la vraie page de création plutôt que d'ouvrir un formulaire réduit :
        c'est là que vivent la photo, la distillerie, le degré. Le paramètre `seance` dit
        d'où l'on vient — la fiche créée rejoint le programme et l'on revient ici ;
        `aveugle` y emporte le choix fait au-dessus.
      */}
      <Link
        className={styles.newRumLink}
        to={`/rhums/nouveau?seance=${session.id}${blind ? '&aveugle=1' : ''}`}
      >
        Elle n’est pas dans la cave ? Créer sa fiche
      </Link>

      <Button variant="ghost" onClick={() => setOpen(false)}>
        Annuler
      </Button>
    </Card>
  );
}
